import {
  // eslint-disable-next-line no-restricted-imports
  $convertFromMarkdownString,
  // eslint-disable-next-line no-restricted-imports
  $convertToMarkdownString,
  Transformer,
} from '@lexical/markdown';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import { ElementNode } from 'lexical';
import { CSSProperties, PropsWithChildren } from 'react';

import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';

import { TextEditorContext } from './contexts/textEditor.context';
import { healFontSizeFormatLeak } from './plugins/FontSizePlugin/healFontSizeFormatLeak';

const BORDER_RADIUS = '12px';

interface TextEditorWrapperProps extends PropsWithChildren {
  height?: CSSProperties['height'];
  border?: CSSProperties['border'];
  borderRadius?: CSSProperties['borderRadius'];
  hideBorder?: boolean;
}
export function TextEditorWrapper({
  children,
  height = '100%',
  border = `1px solid ${COLORS.GRAY[200]}`,
  borderRadius = '6px',
  hideBorder = false,
}: TextEditorWrapperProps) {
  return (
    <Stack
      color={COLORS.PRIMITIVES.BLACK}
      width="100%"
      lineHeight={1.5}
      textAlign="left"
      fontWeight={400}
      height={height}
      overflow="hidden"
      border={hideBorder ? 'none' : border}
      borderRadius={hideBorder ? 0 : borderRadius}
    >
      {children}
    </Stack>
  );
}

export interface EditorInnerProps extends PropsWithChildren {
  borderRadius?: CSSProperties['borderRadius'];
  contentPadding?: CSSProperties['padding'];
  sx?: SxProps<Theme>;
}

export function EditorInner({
  children,
  borderRadius = BORDER_RADIUS,
  contentPadding,
  sx = {},
}: EditorInnerProps) {
  const contentPaddingValue =
    typeof contentPadding === 'number' ? `${contentPadding}px` : contentPadding;

  return (
    <Box
      sx={{
        background: COLORS.PRIMITIVES.WHITE,
        borderRadius,
        height: '100%',
        overflow: 'hidden',
        ...(contentPaddingValue != null && {
          // ContentEditable applies its padding inline, so local overrides need !important.
          [`& #${CONTENT_EDITABLE_ID}`]: {
            padding: `${contentPaddingValue} !important`,
          },
        }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function FlushEditorInner({ children }: PropsWithChildren) {
  return (
    <EditorInner borderRadius={0} contentPadding={0}>
      {children}
    </EditorInner>
  );
}

interface TextEditorContentEditableProps {
  themeVariant: TextEditorContext['theme'];
}

export const CONTENT_EDITABLE_ID = 'text-editor-content-editable';

/**
 * The editor's content-editable element, for callers that drive it through DOM
 * events (see `CopyAllPlugin`).
 *
 * `CONTENT_EDITABLE_ID` is a fixed id, so searching the whole document finds every
 * mounted editor — and more than one is mounted whenever an editor in a trowser
 * sits over a page that has its own (e.g. Edit summary over the document summary).
 * Duplicate ids also make the first match arbitrary, so a document-wide search can
 * silently act on the wrong editor. Pass the `root` containing the editor you mean.
 */
export function getEditorElement(root: ParentNode = document) {
  const maybeEditors = root.querySelectorAll(`#${CONTENT_EDITABLE_ID}`);

  if (maybeEditors.length === 0) {
    diagnostics.error('Could not find editor element');
  }

  if (maybeEditors.length > 1) {
    diagnostics.error('Found multiple editor elements');
  }

  return maybeEditors[0];
}

export function TextEditorContentEditable({
  themeVariant,
}: TextEditorContentEditableProps) {
  if (['contextContent', 'presentationContent'].includes(themeVariant)) {
    return (
      <ContentEditable
        style={{
          resize: 'none',
          fontSize: '15px',
          position: 'relative',
          tabSize: 1,
          outline: 0,
          padding: 0,
        }}
      />
    );
  }

  return (
    <ContentEditable
      id={CONTENT_EDITABLE_ID}
      style={{
        minHeight: '150px',
        resize: 'none',
        fontSize: '15px',
        position: 'relative',
        tabSize: 1,
        outline: 0,
        padding: '15px 10px',
      }}
    />
  );
}

export const EMPTY_EDITOR_JSON = JSON.stringify({
  root: {
    children: [
      {
        children: [],
        direction: null,
        format: '',
        indent: 0,
        type: 'paragraph',
        version: 1,
      },
    ],
    direction: null,
    format: '',
    indent: 0,
    type: 'root',
    version: 1,
  },
});

interface ConvertFromMarkdownStringInput {
  markdown: string;
  transformers?: Array<Transformer>;
  node?: ElementNode;
  shouldPreserveNewLines?: boolean;
  shouldMergeAdjacentLines?: boolean;
}

export function convertFromMarkdownString({
  markdown,
  transformers,
  node,
  shouldPreserveNewLines = false,
  shouldMergeAdjacentLines = false,
}: ConvertFromMarkdownStringInput): void {
  // Heal legacy slides where bold/italic/etc. spans the `[size=N]…[/size]`
  // boundary with unbalanced markers. See healFontSizeFormatLeak.ts and
  // FontSizePlugin.test.ts ("bold spanning FontSize boundary") for context.
  const healed = healFontSizeFormatLeak(markdown);
  return $convertFromMarkdownString(
    healed,
    transformers,
    node,
    shouldPreserveNewLines,
    shouldMergeAdjacentLines
  );
}

interface ConvertToMarkdownStringInput {
  transformers?: Array<Transformer>;
  node?: ElementNode;
  shouldPreserveNewLines?: boolean;
}

export function convertToMarkdownString({
  transformers,
  node,
  shouldPreserveNewLines = false,
}: ConvertToMarkdownStringInput): string {
  return $convertToMarkdownString(transformers, node, shouldPreserveNewLines);
}
